import { type ColorScheme, FLAVOR_FOR_SCHEME, type Flavor } from "./palette";

export type ColorSchemePreference = "system" | ColorScheme;

/** localStorage key for the preference. apps/web/public/theme-init.js reads it before paint. */
export const COLOR_SCHEME_STORAGE_KEY = "codeshare:color-scheme";

const PREFERENCES: readonly ColorSchemePreference[] = ["system", "light", "dark"];
const DARK_QUERY = "(prefers-color-scheme: dark)";

export interface ColorSchemeDeps {
  storage?: Pick<Storage, "getItem" | "setItem"> | null;
  media?: Pick<MediaQueryList, "matches" | "addEventListener" | "removeEventListener"> | null;
  /** Element that receives `data-theme`. */
  root?: { dataset: DOMStringMap } | null;
}

function browserDeps(): ColorSchemeDeps {
  if (typeof window === "undefined") return {};
  let storage: Storage | null = null;
  try {
    storage = window.localStorage;
  } catch {
    // Storage can throw when disabled (e.g. some private modes); fall back to session-only.
  }
  return { storage, media: window.matchMedia(DARK_QUERY), root: document.documentElement };
}

/**
 * Tracks the user's color scheme preference (system/light/dark), resolves it against the OS
 * setting, persists it, and applies the matching Catppuccin flavor as `data-theme`.
 */
export class ColorSchemeController {
  readonly #deps: ColorSchemeDeps;
  readonly #listeners = new Set<() => void>();
  #preference: ColorSchemePreference;

  constructor(deps: ColorSchemeDeps = browserDeps()) {
    this.#deps = deps;
    this.#preference = this.#load();
    deps.media?.addEventListener("change", this.#onSystemChange);
    this.#apply();
  }

  get preference(): ColorSchemePreference {
    return this.#preference;
  }

  get scheme(): ColorScheme {
    if (this.#preference !== "system") return this.#preference;
    return this.#deps.media?.matches ? "dark" : "light";
  }

  get flavor(): Flavor {
    return FLAVOR_FOR_SCHEME[this.scheme];
  }

  setPreference(preference: ColorSchemePreference): void {
    if (preference === this.#preference) return;
    this.#preference = preference;
    try {
      this.#deps.storage?.setItem(COLOR_SCHEME_STORAGE_KEY, preference);
    } catch {}
    this.#apply();
    this.#emit();
  }

  /** Calls `listener` whenever the resolved scheme or the preference changes. */
  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  dispose(): void {
    this.#deps.media?.removeEventListener("change", this.#onSystemChange);
    this.#listeners.clear();
  }

  #load(): ColorSchemePreference {
    let stored: string | null | undefined;
    try {
      stored = this.#deps.storage?.getItem(COLOR_SCHEME_STORAGE_KEY);
    } catch {}
    return PREFERENCES.find((p) => p === stored) ?? "system";
  }

  #apply(): void {
    if (this.#deps.root) this.#deps.root.dataset.theme = this.flavor;
  }

  #emit(): void {
    for (const listener of this.#listeners) listener();
  }

  readonly #onSystemChange = () => {
    if (this.#preference !== "system") return;
    this.#apply();
    this.#emit();
  };
}
